import { useEffect, useState } from 'react'
import { Button, Card, ErrorState, LoadingState, Modal, PageHeader, StatusBadge, Textarea, useToast } from '@/components/ui'
import { customerApi } from '../lib/api'

interface Category {
  key: string
  label: string
  description: string
  weight: number
  status: 'active' | 'partial' | 'missing'
  evidence: string
  upsellItemCode?: string
}

interface ScoreResult {
  score: number
  categories: Category[]
  warnings: string[]
}

function scoreToneClass(score: number): string {
  if (score >= 80) return 'bg-green-600'
  if (score >= 50) return 'bg-brand-700'
  return 'bg-red-600'
}

function ScoreRing({ score }: { score: number }) {
  return (
    <div
      className={`w-32 h-32 rounded-full flex items-center justify-center text-h2 font-bold text-white shadow-card ${scoreToneClass(score)}`}
    >
      {score}/100
    </div>
  )
}

export function CustomerSecurityStatusPage() {
  const [data, setData] = useState<ScoreResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeCategory, setActiveCategory] = useState<Category | null>(null)
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    customerApi.get<ScoreResult>('/security-status')
      .then((res) => {
        setData(res.data)
        res.data.warnings.forEach((w) =>
          toast({ title: 'Hinweis', description: w, tone: 'warning' }),
        )
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }, [toast])

  const submitInterest = async () => {
    if (!activeCategory) return
    setSubmitting(true)
    try {
      const res = await customerApi.post<{ tanssTicketId?: string }>('/upsell-interest', {
        categoryKey: activeCategory.key,
        message: message.trim() || undefined,
      })
      const ticketId = res.data?.tanssTicketId
      toast({
        title: 'Anfrage gesendet',
        description: ticketId
          ? `Dein Ansprechpartner meldet sich zeitnah (Ticket #${ticketId}).`
          : 'Dein Ansprechpartner meldet sich zeitnah.',
        tone: 'success',
      })
      setActiveCategory(null)
      setMessage('')
    } catch (err) {
      const ax = err as { response?: { data?: { error?: string; message?: string } } }
      const msg = ax.response?.data?.error ?? ax.response?.data?.message ?? 'Anfrage fehlgeschlagen'
      toast({ title: 'Fehler', description: msg, tone: 'danger' })
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <LoadingState />
  if (error) return <ErrorState details={error} />
  if (!data) return null

  return (
    <div>
      <PageHeader title="Sicherheitsstatus" description="Dein aktueller Schutz im Überblick" />

      <Card>
        <Card.Body>
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <ScoreRing score={data.score} />
            <div>
              <div className="text-body-lg font-semibold">Dein Security-Score</div>
              <p className="text-body text-gray-600 max-w-xl">
                Je höher der Score, desto umfassender bist du gegen typische IT-Risiken geschützt.
                Lücken kannst du mit einem Klick an deinen Ansprechpartner melden – dieser erstellt ein passendes Angebot.
              </p>
            </div>
          </div>
        </Card.Body>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {data.categories.map((cat) => (
          <Card key={cat.key}>
            <Card.Body>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-body font-semibold">{cat.label}</div>
                  <p className="text-caption text-gray-500 mt-1">{cat.description}</p>
                </div>
                <StatusBadge status={`security_${cat.status}`} />
              </div>
              <div className="text-caption text-gray-600 mt-3">{cat.evidence}</div>
              {cat.status !== 'active' && (
                <div className="mt-3">
                  <Button
                    variant={cat.status === 'missing' ? 'primary' : 'secondary'}
                    size="sm"
                    onClick={() => setActiveCategory(cat)}
                  >
                    {cat.status === 'missing' ? 'Angebot anfordern' : 'Abdeckung erweitern'}
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        ))}
      </div>

      <Modal
        open={!!activeCategory}
        onOpenChange={(o) => { if (!o) { setActiveCategory(null); setMessage('') } }}
        title={activeCategory ? `Interesse: ${activeCategory.label}` : ''}
        footer={
          <>
            <Button variant="ghost" onClick={() => { setActiveCategory(null); setMessage('') }}>
              Abbrechen
            </Button>
            <Button variant="primary" onClick={submitInterest} loading={submitting}>
              Anfrage senden
            </Button>
          </>
        }
      >
        <p className="text-body text-gray-600 mb-3">
          Dein Ansprechpartner bei implec erhält eine Anfrage zu „{activeCategory?.label}“ und meldet sich zeitnah.
        </p>
        <Textarea
          label="Optionale Nachricht"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Spezielle Anforderungen, Anzahl Arbeitsplätze, Zeitrahmen …"
          rows={4}
        />
      </Modal>
    </div>
  )
}
